import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {TextField} from "@khanacademy/wonder-blocks-form";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {LabeledField} from "@khanacademy/wonder-blocks-labeled-field";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";

import {ImagePreview} from "../image-preview.tsx";

import {PREVIEW_HEIGHT, PREVIEW_WIDTH} from "./constants.ts";
import {sharedStyles} from "./shared-styles.ts";
import type {SharedModalContentProps} from "./types.ts";

import imageIcon from "@phosphor-icons/core/regular/image.svg";
import trashIcon from "@phosphor-icons/core/regular/trash-simple.svg";

interface Props extends SharedModalContentProps {
    // The image URL for preview.
    imageURL: string;
    // Error message for the URL input, or null if none.
    error?: string | null;
    // The current value of the URL input field.
    pendingImageURL: string;
    // Callback to set the value of the URL input field.
    setPendingImageURL: (url: string) => void;
    // Callback to fetch and set the image from the URL.
    handleSetImageURL: () => void;
    // Callback to clear the fetched image.
    handleClearImage: () => void;
    // The name of the selected file, or null if none.
    selectedFilename: string | null;
    // Whether the preview button is in a loading state.
    loading?: boolean;
}

/**
 * Renders the URL input experience for the image attachment modal.
 * Displays a text field for entering an image URL and a preview of the fetched image.
 * Used as a content component within the image attach modal wrapper.
 */
export const UrlModalContent = ({
    image,
    imageURL,
    error,
    pendingImageURL,
    setPendingImageURL,
    handleSetImageURL,
    handleClearImage,
    selectedFilename,
    loading = false,
}: Props) => (
    <View style={{width: "100%"}}>
        <View style={styles.urlContainer}>
            <LabeledField
                label={t`Insert URL`}
                styles={{
                    root: {
                        flex: 1,
                    },
                }}
                contextLabel={t`required`}
                field={
                    <TextField
                        disabled={!!imageURL}
                        placeholder="https://khanacademy.org/algebra1/..."
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                handleSetImageURL();
                            }
                        }}
                        onBlur={() => null}
                        value={pendingImageURL}
                        onChange={setPendingImageURL}
                        required={true}
                    />
                }
                errorMessage={error}
            />
            <Strut size={spacing.medium_16} />
            <View>
                <Strut size={spacing.xLarge_32} />
                <Button
                    style={{flexShrink: 0}}
                    disabled={Boolean(!pendingImageURL || imageURL || loading)}
                    kind="secondary"
                    onClick={handleSetImageURL}
                    aria-label={t`Preview image`}
                    spinner={loading}
                >
                    {t`Preview`}
                </Button>
            </View>
        </View>
        <View style={[sharedStyles.previewContainer, styles.previewContainer]}>
            {image && !error && image.imageURL ? (
                <ImagePreview
                    image={image.imageURL}
                    displayWidth={PREVIEW_WIDTH}
                    displayHeight={PREVIEW_HEIGHT}
                />
            ) : null}
            {!image && (
                <PhosphorIcon icon={imageIcon} size="medium" color="#5F6167" />
            )}
        </View>
        <Strut size={spacing.small_12} />
        {image && (
            <View style={sharedStyles.imageFilenameContainer}>
                <View style={sharedStyles.imageFilenameRow}>
                    <PhosphorIcon
                        icon={imageIcon}
                        size="medium"
                        aria-hidden={true}
                    />
                    <LabelMedium style={sharedStyles.imageFilenameOverflow}>
                        {selectedFilename}
                        {imageURL}
                    </LabelMedium>
                </View>
                <IconButton
                    icon={trashIcon}
                    kind="tertiary"
                    size="small"
                    onClick={handleClearImage}
                    aria-label={t`Clear image`}
                    testId="image-attach-clear-button"
                />
            </View>
        )}
    </View>
);

const styles = StyleSheet.create({
    urlContainer: {
        width: "100%",
        flexDirection: "row",
        justifyItems: "flex-end",
        justifyContent: "space-between",
        paddingBlockEnd: spacing.medium_16,
    },
    previewContainer: {
        backgroundColor: "#F3F4F5",
    },
});
